import type { SelectOption } from '../../../../components/inputs/selectShared';
import type { PlantillaVacantItem } from '../personnelTypes';

/**
 * One option per POSITION, not per slot.
 *
 * The feed is a flat list of vacant items — the individual numbered slots — and a picker built
 * straight from it lists the same designation once per opening, which reads as three different
 * jobs rather than one job with three seats. Grouped, the reader sees the position, the unit it
 * sits in, and how many seats are open.
 *
 * Grouped on the position's **id**, never its title: two units can staff the same designation,
 * and two positions in one unit can share it.
 */
export function vacantPositionOptions(items: PlantillaVacantItem[]): SelectOption[] {
  const groups = new Map<string, PlantillaVacantItem[]>();

  for (const item of items) {
    const held = groups.get(item.plantillaPositionId);

    if (held) {
      held.push(item);
    } else {
      groups.set(item.plantillaPositionId, [item]);
    }
  }

  return [...groups.values()].map((group) => {
    const [first] = group;

    return {
      value: first.plantillaPositionId,
      label: first.positionTitle,

      // The unit reads under the designation: it answers "which one" once two units staff the
      // same job, and it is not what the reader scans for first.
      sublabel: first.orgUnitName,

      // Right-aligned and muted, like every other trailing count in a picker.
      right: `${group.length} vacant`,

      /*
        The unit and the item numbers stay SEARCHABLE even though only the unit is displayed:
        a reader who knows the slot number should still find it, and dropping the text from the
        index would make the search narrower than what the list used to match.
      */
      keywords: [first.orgUnitName, ...group.map((i) => i.itemNumber)].join(' '),
    };
  });
}

/**
 * The open SLOTS of one position.
 *
 * The slot is the row a service record is written against, and from 15 Sep 2026 the reader
 * chooses it rather than the server allocating the lowest-numbered one. The cost is stated
 * where the rule is: this list is a moment old, so the server re-checks vacancy on write and
 * a slot taken in between comes back as a rejection rather than a double booking.
 *
 * Ordered by item number, which is how the plantilla reads and how the server used to fill.
 */
export function vacantSlotOptions(
  items: PlantillaVacantItem[],
  positionId: string | null,
): SelectOption[] {
  if (positionId === null) return [];

  return items
    .filter((item) => item.plantillaPositionId === positionId)
    .sort((a, b) => a.itemNumber.localeCompare(b.itemNumber))
    .map((item) => ({ value: item.id, label: item.itemNumber }));
}
